
import { supabase } from "@/integrations/supabase/client";
import { FolderShareWithUser } from "../types";

/**
 * Fetches all shares for a specific folder that the current user owns
 */
export const getFolderShares = async (folderId: string): Promise<FolderShareWithUser[]> => {
  console.log(`Fetching shares for folder ${folderId}`);
  
  try {
    // First fetch all shares
    const { data: sharesData, error: sharesError } = await supabase
      .from("folder_shares")
      .select(`
        id,
        folder_id,
        owner_id,
        shared_with_email,
        shared_with_id,
        created_at,
        notification_shown,
        accepted
      `)
      .eq("folder_id", folderId);
    
    if (sharesError) {
      console.error("Error fetching folder shares:", sharesError);
      throw new Error(`Kunde inte hämta delningar: ${sharesError.message}`);
    }
    
    console.log(`Found ${sharesData?.length || 0} shares for folder ${folderId}`);
    
    // For each share with a shared_with_id, fetch the user profiles data separately
    const sharesWithProfiles = await Promise.all((sharesData || []).map(async (share) => {
      if (share.shared_with_id) {
        try {
          // Fetch user profile using shared_with_id
          const { data: profileData, error: profileError } = await supabase
            .from("user_profiles")
            .select("first_name, last_name")
            .eq("id", share.shared_with_id)
            .single();
          
          if (profileError) {
            console.log(`Could not find user profile for id ${share.shared_with_id}, no fallback available`);
            
            // Return share without user profile data since profiles table is removed
            return {
              ...share,
              user_profiles: {
                first_name: "",
                last_name: ""
              }
            } as FolderShareWithUser;
          } else if (profileData) {
            return {
              ...share,
              user_profiles: profileData
            } as FolderShareWithUser;
          }
        } catch (profileFetchError) {
          console.error(`Error fetching profile for user ${share.shared_with_id}:`, profileFetchError);
        }
      }
      
      // If no profile was found or there was an error, return the share with null user_profiles
      return {
        ...share,
        user_profiles: null
      } as FolderShareWithUser;
    }));
    
    return sharesWithProfiles;
  } catch (error: any) {
    console.error("Error in getFolderShares:", error);
    throw error;
  }
};

/**
 * Removes a share for a specific folder
 */
export const removeShare = async (shareId: string): Promise<boolean> => {
  console.log(`Removing share ${shareId}`);
  
  try {
    const { error } = await supabase
      .from("folder_shares")
      .delete()
      .eq("id", shareId);
    
    if (error) {
      console.error("Error removing share:", error);
      throw new Error(`Kunde inte ta bort delning: ${error.message}`);
    }
    
    console.log(`Successfully removed share ${shareId}`);
    return true;
  } catch (error: any) {
    console.error("Error in removeShare:", error);
    throw error;
  }
};

